/**
 * @copyright 丽汽智贸平台 V1.0
 * @year 2026
 * @file index.js
 * @description API 接口封装
 */
import http from "../utils/http";
import crypto from "../utils/crypto";

/**
 * 手机号验证码登录
 * 手机号使用 DES 加密后传输
 * @param {Object} params - { mobile: 手机号, code: 验证码, rememberMe: 是否记住我 }
 */
export function loginByPhoneAndCode(params) {
  const encryptedData = {
    mobile: crypto.encryptPhone(params.mobile),
    code: params.code,
    rememberMe: params.rememberMe || false,
    browser: params.browser || "",
    os: params.os || "",
    deviceInfo: params.deviceInfo || "",
  };
  return http.post("/api/user/LoginByPhoneAndCode", encryptedData);
}

/**
 * 发送短信验证码
 * 手机号使用 DES 加密后传输
 * @param {Object} params - { phoneNo: 手机号 }
 */
export function getPhoneCode(params) {
  const encryptedPhone = crypto.encryptPhone(params.phoneNo);
  return http.post("/api/user/GetPoneCode", { phone: encryptedPhone });
}

/**
 * 获取钉钉 corpId（免登发起 authCode 前调用，无需登录态）
 */
export function getDingTalkCorpId() {
  return http.get("/api/User/DingTalkCorp");
}

/**
 * 获取钉钉 appKey（OAuth2 授权 URL 的 client_id，无需登录态）
 */
export function getDingTalkClientId() {
  return http.get("/api/User/DingTalkClientId");
}

/**
 * 钉钉 OAuth2 免登登录：授权 code 换 token
 * @param {Object} params - { code: OAuth 授权码, browser/os/deviceInfo: 设备信息(可选) }
 */
export function dingTalkOAuthLogin(params) {
  return http.post("/api/User/DingTalkOAuthLogin", params);
}

/**
 * 钉钉免登登录：authCode 换登录 token
 * @param {Object} params - { code: 免登授权码, browser/os/deviceInfo: 设备信息(可选) }
 */
export function dingTalkLogin(params) {
  return http.post("/api/User/DingTalkLogin", params);
}

/**
 * 获取用户菜单权限
 */
export function getUserMenus() {
  return http.get("/api/User/GetUserMenus");
}

/**
 * 获取用户所有权限列表
 */
export function getUserPermissions() {
  return http.get("/api/User/GetUserPermissions");
}

/**
 * 获取用户单位信息
 */
export function getUserUnitInfo() {
  return http.get("/api/User/GetUserUnitInfo");
}

/**
 * 获取登录历史记录
 * @param {number} top - 获取条数
 */
export function getLoginHistory(top = 10) {
  return http.get(`/api/User/GetLoginHistory?top=${top}`);
}

/**
 * 获取待盘点任务
 */
export function getPendingTasks() {
  return http.get("/api/Inventory/GetPendingTasks");
}

/**
 * 上传视频或照片并提交盘点
 * @param {FormData} formData - 包含 recordId, video, remark
 */
export function uploadAndCheck(formData) {
  return http.post("/api/Inventory/UploadAndCheck", formData, {
    //headers: { "Content-Type": "multipart/form-data" },
    timeout: 300000,
  });
}

/**
 * 获取已盘点记录
 * @param {number} page - 页码
 * @param {number} pageSize - 每页条数
 */
export function getCheckedRecords(page = 1, pageSize = 20) {
  return http.get(`/api/Inventory/GetCheckedRecords?page=${page}&pageSize=${pageSize}`);
}

/**
 * 获取业务看板数据
 * @param {string} beginDate - 开始日期
 * @param {string} endDate - 结束日期
 */
export function getDashboardData(beginDate, endDate) {
  return http.get(`/api/Dashboard/GetDashboardData?beginDate=${beginDate}&endDate=${endDate}`);
}

/**
 * 获取盘点媒体文件列表
 * @param {number} recordId - 盘点记录ID
 * @param {string} fileType - 文件类型: photo/video，不传则返回全部
 */
export function getMediaList(recordId, fileType = null) {
  let url = `/api/Inventory/GetMediaList/${recordId}`;
  if (fileType) url += `?fileType=${fileType}`;
  return http.get(url);
}

/**
 * 删除单个媒体文件
 * @param {number} formData - 媒体文件ID
 */
export function deleteCheckRecord(formData) {
  return http.post("/api/Inventory/DeleteCheckRecord", formData);
}

/**
 * 获取盘点用分类（车辆权属下：登记证书/行驶证/检测报告/实车照片）
 */
export function getCheckCategories() {
  return http.get("/api/Inventory/CheckCategories");
}

/**
 * 按分类上传车辆权属照片（NAS + 订单材料附件v2）
 * @param {FormData} formData - recordNo/orderNo/vin/categoryId/remark/latitude/longitude/locationName/locationAccuracy/photos
 */
export function uploadVehicleMaterials(formData) {
  return http.post("/api/Inventory/UploadVehicleMaterials", formData, {
    timeout: 300000,
  });
}

/**
 * 获取某车某分类已传材料列表
 */
export function getVehicleMaterials(params) {
  return http.get(
    `/api/Inventory/VehicleMaterials?orderNo=${encodeURIComponent(
      params.orderNo || ""
    )}&vin=${encodeURIComponent(params.vin || "")}&categoryId=${
      params.categoryId || 0
    }`
  );
}

/**
 * 读取 NAS 材料文件内容（Blob，带鉴权；用于预览）
 */
export function getMaterialFileBlob(attachmentId) {
  return http.get(`/api/Inventory/MaterialFile/${attachmentId}`, {
    responseType: "blob",
  });
}

/**
 * 删除某条车辆权属材料（附件行 + NAS 文件）
 */
export function deleteVehicleMaterial(attachmentId) {
  return http.delete(`/api/Inventory/DeleteVehicleMaterial/${attachmentId}`);
}

/**
 * 批量识别照片中的车架号（百度OCR，不落库）
 * @param {FormData} formData - photos 多张
 */
export function batchRecognize(formData) {
  return http.post("/api/Inventory/BatchRecognize", formData, {
    timeout: 300000,
  });
}

/**
 * 某订单全部采购车辆（批量盘点 VIN 匹配用）
 */
export function getOrderVehicles(orderNo) {
  return http.get(
    `/api/Inventory/OrderVehicles?orderNo=${encodeURIComponent(orderNo || "")}`
  );
}

/**
 * 获取审批详情
 * @param {string} approvalNo - 审批单号
 */
export function approvalDetail(approvalNo) {
  return http.get(
    `/api/Approval/Detail?approvalNo=${encodeURIComponent(approvalNo || "")}`
  );
}

/**
 * 提交审批操作（通过/驳回/撤销）
 * @param {Object} params - { approvalNo, action: approve|reject|withdraw, opinion }
 */
export function approvalAct(params) {
  return http.post("/api/Approval/Act", params || {});
}

/**
 * 我的审批列表
 * @param {string} filter - todo|initiated|cc
 */
export function approvalMine(filter = "") {
  return http.get(
    `/api/Approval/Mine?filter=${encodeURIComponent(filter || "")}`
  );
}

/**
 * 读取审批附件文件内容（Blob，带鉴权；比价图片/合同文件/附件 预览下载）
 * @param {number} seq - 审批附件 序号
 */
export function getApprovalAttachmentBlob(seq) {
  return http.get(`/api/Approval/AttachmentFile/${seq}`, {
    responseType: "blob",
  });
}

/**
 * 读取资金审批附件（历史单据仅存 NAS，按 审批单号+文件名）
 * @param {string} approvalNo - 审批单号
 * @param {string} fileName - 文件名
 */
export function getFundAttachmentBlob(approvalNo, fileName) {
  return http.get(
    `/api/Approval/FundAttachmentFile?approvalNo=${encodeURIComponent(
      approvalNo || ""
    )}&fileName=${encodeURIComponent(fileName || "")}`,
    { responseType: "blob" }
  );
}

export default {};
